import { track } from '@/common/track';
import { COURSE_TYPE } from '@/constants';
import { useLocale } from '@/providers/LocaleProvider';
import IconOpenedBook from '@/resources/images/icon-opened-book.svg';
import CertificateIcon from '@/resources/images/icons-certificate.svg';
import CourseIcon from '@/resources/images/icons-course.svg';
import DiplomaIcon from '@/resources/images/icons-diploma.svg';
import { KLAYO_COLORS } from '@/themes';
import IntegrationAbbreviation from '@/views/settings/IntegrationAbbreviation';
import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import { ManageTrainingEvent } from 'klayowebshared';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useNavigate } from 'react-router';
import { AppCard } from './AppCard';
import { ManageTrainingTableRow } from './table/ManageTrainingTable';

interface ManageTrainingCardProps {
  item: ManageTrainingTableRow;
}

export const ManageTrainingCard = ({ item }: ManageTrainingCardProps) => {
  const { translate } = useLocale();
  const navigate = useNavigate();

  const hasEnrolments =
    (item?.enrolledCount || 0) > 0 ||
    (item?.completedCount || 0) > 0 ||
    (item?.inProgressCount || 0) > 0;

  const handleCourseClick = () => {
    if (item.integrationSource !== null) return;

    track(ManageTrainingEvent.CoursesViewCourse, { courseName: item.courseName });
    navigate(`/settings/learning/${item.id}`);
  };

  const getCourseTypeIcon = (courseType: string) => {
    switch (courseType) {
      case COURSE_TYPE.DIPLOMA:
        return <DiplomaIcon width={20} height={20} color={KLAYO_COLORS.GreyGR900} />;
      case COURSE_TYPE.CERTIFICATE:
        return <CertificateIcon width={20} height={20} color={KLAYO_COLORS.GreyGR900} />;

      default:
        return <CourseIcon width={20} height={20} color={KLAYO_COLORS.GreyGR900} />;
    }
  };

  return (
    <AppCard
      onClick={handleCourseClick}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Box
        sx={{
          position: 'relative',
        }}
      >
        {item.thumbnailUrl ? (
          <LazyLoadImage
            className='klayo_card_image'
            src={item.thumbnailUrl}
            alt={item.courseName}
            effect='blur'
            width={'100%'}
          />
        ) : (
          <Box
            sx={{
              aspectRatio: '16/10',
              width: '100%',
              borderRadius: '8px 8px 0 0',
              background: `linear-gradient(360deg, ${KLAYO_COLORS.GreyGR200} 0%, ${KLAYO_COLORS.WhiteWH900} 100%)`,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flex: 1,
            }}
          >
            <IconOpenedBook width={80} height={80} color={KLAYO_COLORS.GreyGR200} />
          </Box>
        )}
        {item?.courseType ? (
          <Stack width={'max-content'} position={'absolute'} bottom={'-12px'} left={'24px'}>
            <Chip
              sx={{
                backgroundColor: `${KLAYO_COLORS.WhiteWH900} !important`,
                border: `1px solid ${KLAYO_COLORS.GreyGR900} !important`,

                '& .MuiChip-label': {
                  color: KLAYO_COLORS.GreyGR900,
                  fontWeight: 500,
                  fontSize: '14px',
                },
              }}
              label={item.courseType}
              icon={getCourseTypeIcon(item.courseType)}
            />
          </Stack>
        ) : null}
      </Box>

      <Stack spacing={3} sx={{ padding: '24px' }} flex={1} justifyContent={'space-between'}>
        <Stack gap={1}>
          <Typography variant='h6' fontWeight={600} fontSize={18}>
            {item.courseName}{' '}
            <IntegrationAbbreviation
              data={item}
              type={IntegrationAbbreviation.dataType.integrationUser.value}
            />
          </Typography>

          {/* <Stack direction='row'>
            <Rating
              value={item.numberOfStars}
              max={5}
              size='small'
              readOnly
              precision={0.5}
              sx={{
                '& .MuiRating-iconFilled': {
                  color: KLAYO_COLORS.GreyGR900,
                },
                '& .MuiRating-iconHover': {
                  color: KLAYO_COLORS.GreyGR900,
                },
                '& .MuiRating-iconEmpty': {
                  color: KLAYO_COLORS.GreyGR500,
                },
              }}
            />
          </Stack> */}
        </Stack>

        {hasEnrolments ? (
          <Stack direction='row' spacing={1.5} justifyContent={'space-between'}>
            <Stack>
              <Typography variant='body1' fontWeight={600}>
                {item.enrolledCount}
              </Typography>
              <Typography variant='caption' color={KLAYO_COLORS.GreyGR500}>
                {translate('manage.enrolled')}
              </Typography>
            </Stack>
            <Divider orientation='vertical' flexItem />
            <Stack>
              <Typography variant='body1' fontWeight={600}>
                {item.completedCount}
              </Typography>
              <Typography variant='caption' color={KLAYO_COLORS.GreyGR500}>
                {translate('manage.completed')}
              </Typography>
            </Stack>
            <Divider orientation='vertical' flexItem />
            <Stack>
              <Typography variant='body1' fontWeight={600}>
                {item.inProgressCount}
              </Typography>
              <Typography variant='caption' color={KLAYO_COLORS.GreyGR500}>
                {translate('manage.inProgress')}
              </Typography>
            </Stack>
          </Stack>
        ) : (
          <Typography variant='body2' color={KLAYO_COLORS.GreyGR500}>
            {translate('manage.noEnrolments')}
          </Typography>
        )}
      </Stack>
    </AppCard>
  );
};
